'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import type {
  AttachmentTranslationStatus,
  AttachmentTranslationUnsupportedReason,
} from '@govinity/validation';

/**
 * `translationStatus` is absent unless the reader's locale is one the government has
 * enabled, so its presence is the gate — a source-language reader triggers none of this.
 */
export type TranslatableDocument = {
  id: number;
  translationStatus?: AttachmentTranslationStatus | null;
  translationReason?: AttachmentTranslationUnsupportedReason | null;
};

type DocumentTranslations = {
  request: (document: TranslatableDocument) => void;
  /**
   * True from the click that requests a translation until it lands, covering the gap before the
   * server reports `pending` so a viewer never flashes the untranslated file before blocking.
   */
  isTranslating: (document: TranslatableDocument) => boolean;
};

/** Default for surfaces with no translation endpoint: they render and open normally. */
const noTranslations: DocumentTranslations = { request: () => {}, isTranslating: () => false };

const DocumentTranslationsContext = createContext(noTranslations);

export const useDocumentTranslations = () => useContext(DocumentTranslationsContext);

const POLL_MS = 4000;

const MAX_POLLS = 200;

type PollableQuery<T> = { state: { data?: T; dataUpdateCount: number } };

/**
 * `refetchInterval` that keeps a query fresh while any document the selector returns is
 * still translating, so an open viewer swaps to the translated file the moment it lands.
 */
export const translationPollInterval =
  <T,>(selectDocuments: (data: T) => (TranslatableDocument | null)[] | undefined) =>
  ({ state }: PollableQuery<T>) => {
    const documents = state.data === undefined ? [] : (selectDocuments(state.data) ?? []);
    const isTranslating = documents.some((document) => document?.translationStatus === 'pending');

    return isTranslating && state.dataUpdateCount < MAX_POLLS ? POLL_MS : false;
  };

type TranslatableDocumentsProviderProps = {
  children: React.ReactNode;
  /** Requests translation of one document from whichever endpoint owns it. */
  requestTranslation: (documentId: number) => Promise<unknown>;
  /** Query holding the documents, refetched so the new status and URL arrive. */
  queryKey: QueryKey;
};

export function TranslatableDocumentsProvider({
  children,
  requestTranslation,
  queryKey,
}: TranslatableDocumentsProviderProps) {
  const queryClient = useQueryClient();
  const [requestedIds, setRequestedIds] = useState<ReadonlySet<number>>(new Set());

  const { mutate } = useMutation({
    mutationFn: requestTranslation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onSettled: (_data, _error, documentId) => {
      setRequestedIds((previous) => {
        const remaining = new Set(previous);
        remaining.delete(documentId);
        return remaining;
      });
    },
  });

  // `failed` is retryable — the server flips that row back to pending and re-enqueues, so a
  // translator outage or a transient OpenAI error does not strand a document in English forever.
  // `unsupported` stays terminal: no text layer, or not a PDF, is not going to change on a retry.
  const request = useCallback(
    ({ id, translationStatus }: TranslatableDocument) => {
      if (translationStatus !== null && translationStatus !== 'failed') return;
      setRequestedIds((previous) => new Set(previous).add(id));
      mutate(id);
    },
    [mutate]
  );

  // The second clause covers only an in-flight request — the click before the server has answered,
  // including a retried `failed` row whose status has not flipped back yet. It cannot outlive the
  // request, so a job that fails later shows as failed rather than translating.
  const isTranslating = useCallback(
    ({ id, translationStatus }: TranslatableDocument) =>
      translationStatus === 'pending' ||
      ((translationStatus === null || translationStatus === 'failed') && requestedIds.has(id)),
    [requestedIds]
  );

  const value = useMemo(() => ({ request, isTranslating }), [request, isTranslating]);

  return (
    <DocumentTranslationsContext.Provider value={value}>
      {children}
    </DocumentTranslationsContext.Provider>
  );
}
